import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { DEPRECATED_PREFIXES, STORAGE_PREFIXES } from '@/hooks/useLocalStorageManager';
import { kpiTracker } from '@/lib/kpiTracker';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class AppErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is a chunk loading error (stale deployment)
    const isChunkLoadError = 
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('ChunkLoadError') ||
      error.message.includes('Loading CSS chunk') ||
      error.message.includes('Unable to preload CSS');
    
    if (isChunkLoadError) {
      logger.warn('Chunk loading error detected, auto-reloading...', {
        message: error.message
      });
      // Force reload to get fresh chunks
      window.location.reload();
      return;
    }
    
    logger.error('AppErrorBoundary caught an error:', error, errorInfo);
    
    // Log additional context for debugging
    logger.error('Error details:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorBoundary: 'AppErrorBoundary'
    });
    
    // Track error in KPI system
    kpiTracker.recordKPI('error_boundary_count', 1, {
      threshold: 5, // Alert if more than 5 errors
      context: {
        errorMessage: error.message,
        componentStack: errorInfo.componentStack?.split('\n')[0] // First line only
      }
    });
    
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleRecoveryMode = () => {
    try {
      // Clear all localStorage data that might be causing issues using standardized patterns
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          // Use centralized deprecated prefixes
          DEPRECATED_PREFIXES.some(prefix => key.startsWith(prefix)) ||
          // Clear specific problematic patterns
          key.startsWith('sb-') ||
          key.includes('debug') ||
          key.includes('cache') ||
          // Keep only standardized app keys
          !Object.values(STORAGE_PREFIXES).some(prefix => key.startsWith(prefix))
        )) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Also clear session storage
      sessionStorage.clear();
      
      logger.info('Recovery mode: Cleared problematic storage data using standardized cleanup');
      
      // Reset error state and reload
      this.setState({ hasError: false, error: undefined, errorInfo: undefined });
      window.location.reload();
    } catch (clearError) {
      logger.error('Error during recovery mode:', clearError);
      // Fallback to simple reload
      window.location.reload();
    }
  };

  private handleRetry = () => {
    // ✅ FIX: Force full page reload to remount all Providers
    // Prevents stale context errors (e.g., "useSpecsContext must be used within SpecsProvider")
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-lg mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Ứng dụng gặp lỗi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">
                  Có lỗi không mong muốn xảy ra. Vui lòng thử một trong các cách khắc phục dưới đây:
                </p>
                
                {this.state.error && (
                  <details className="mt-3 p-2 bg-muted rounded text-xs">
                    <summary className="cursor-pointer font-medium">Chi tiết lỗi (cho nhà phát triển)</summary>
                    <div className="mt-2 space-y-1">
                      <p><strong>Lỗi:</strong> {this.state.error.message}</p>
                      {this.state.error.stack && (
                        <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-32">
                          {this.state.error.stack}
                        </pre>
                      )}
                    </div>
                  </details>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={this.handleRetry}
                  className="w-full"
                  variant="default"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Tải lại trang
                </Button>
                
                <Button 
                  onClick={this.handleRecoveryMode}
                  className="w-full"
                  variant="destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Chế độ khôi phục (Xóa dữ liệu local)
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Nếu lỗi vẫn tiếp tục, vui lòng liên hệ hỗ trợ kỹ thuật.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}